import { withAlpha, type Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import { DragGhost } from "../core/dragdrop.js";
import type { DragPayload, MouseEventArgs } from "../core/events.js";
import { clamp, type Rect } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import {
  ITEM_DRAG_KIND,
  itemTooltip,
  rarityOf,
  type ItemDragData,
  type ItemStack,
  type RarityTable,
} from "./items.js";

export interface ItemSlotOptions extends ControlOptions {
  item?: ItemStack | null;
  /** Shown in a corner: "1", "Q", "RMB". */
  keybind?: string;
  /** Seconds left on a cooldown, and how long it was. */
  cooldown?: number;
  cooldownMaximum?: number;
  /** A slot the player has not unlocked yet: dimmed and undraggable. */
  locked?: boolean;
  /** Draw the slot as picked out — a valid drop target, a matching filter. */
  highlighted?: boolean;
  /** Draw the slot as the cell the keyboard is on. See `ItemSlot.current`. */
  current?: boolean;
  /** Let the player pick the item up out of this slot. */
  draggable?: boolean;
  /** Take items dropped on it. */
  droppable?: boolean;
  /** Index within the owning grid or bar, carried on the drag payload. */
  index?: number;
  rarities?: RarityTable;
  /** Build the tooltip; defaults to `itemTooltip`. */
  describe?: ((item: ItemStack, theme: Theme) => ReturnType<typeof itemTooltip>) | null;
  countFont?: FontSpec | null;
  onActivate?: (item: ItemStack | null) => void;
}

/** How far the pointer must travel before a press becomes a drag. */
const DRAG_THRESHOLD = 4;
const CORNER_PADDING = 3;
const ICON_INSET = 4;

/**
 * One inventory or hotbar cell: an icon, a stack count, a rarity border, a
 * cooldown sweep and a keybind.
 *
 * This is the atom every other item control is built from — `InventoryGrid`
 * and `Hotbar` are both arrangements of these — and it is deliberately
 * self-contained: give it an `item` and it draws, drags and describes itself
 * with no container's help.
 *
 * A press does not become a drag until the pointer has moved a few pixels, so
 * clicking a slot to use what is in it still works. The drag carries the item
 * and the slot's index, and the slot does *not* empty itself on pickup — the
 * owning container decides what a move means, and a drag that ends nowhere
 * has to leave the inventory as it found it.
 */
export class ItemSlot extends Control {
  #item: ItemStack | null;
  #pressAt: { x: number; y: number } | null = null;
  #dragging = false;

  keybind: string;
  cooldown: number;
  cooldownMaximum: number;
  locked: boolean;
  highlighted: boolean;
  /**
   * This is the cell the keyboard is on.
   *
   * Distinct from `focused`, and it has to be: a grid is a *single* tab stop
   * with a cursor roving inside it, so the ring is drawn on a slot that does
   * not itself hold focus. Owned by the container — `InventoryGrid` and
   * `Hotbar` set it on one slot and clear it when they lose focus.
   */
  current: boolean;
  draggable: boolean;
  droppable: boolean;
  index: number;
  rarities: RarityTable | undefined;
  describe: ItemSlotOptions["describe"];
  countFont: FontSpec | null;

  constructor(options: ItemSlotOptions = {}) {
    super({ width: 44, height: 44, tabStop: true, ...options });
    this.#item = options.item ?? null;
    this.keybind = options.keybind ?? "";
    this.cooldown = Math.max(0, options.cooldown ?? 0);
    this.cooldownMaximum = Math.max(0, options.cooldownMaximum ?? 0);
    this.locked = options.locked ?? false;
    this.highlighted = options.highlighted ?? false;
    this.current = options.current ?? false;
    this.draggable = options.draggable ?? true;
    this.droppable = options.droppable ?? true;
    this.index = options.index ?? -1;
    this.rarities = options.rarities;
    this.describe = options.describe ?? null;
    this.countFont = options.countFont ?? null;
    if (options.onActivate) this.on("itemactivate", () => options.onActivate!(this.#item));

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", () => this.#endPress());
    // Registered once, not per drag: the session raises this on the payload
    // source however the drag ended, including a cancel.
    this.on("dragend", () => {
      this.#dragging = false;
      this.invalidate();
    });
    this.on("click", () => this.activate());
    this.on("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      this.activate();
      e.handled = true;
    });
    this.#syncTooltip();
  }

  get item(): ItemStack | null {
    return this.#item;
  }
  set item(v: ItemStack | null) {
    if (this.#item === v) return;
    this.#item = v;
    this.#syncTooltip();
    this.emit("change");
    this.invalidate();
  }

  get empty(): boolean {
    return this.#item === null;
  }

  /** Fraction of the cooldown still to run, 0..1. */
  get cooldownFraction(): number {
    if (this.cooldownMaximum <= 0) return 0;
    return clamp(this.cooldown / this.cooldownMaximum, 0, 1);
  }

  /** Start a cooldown of `seconds`, or clear one with 0. */
  startCooldown(seconds: number): void {
    this.cooldownMaximum = Math.max(0, seconds);
    this.cooldown = this.cooldownMaximum;
    this.invalidate();
  }

  /** Run the cooldown down by `deltaSeconds`. Driven by the game's own clock. */
  tickCooldown(deltaSeconds: number): void {
    if (this.cooldown <= 0) return;
    this.cooldown = Math.max(0, this.cooldown - deltaSeconds);
    this.invalidate();
  }

  /** Use what is in the slot. Silent when empty, locked or cooling down. */
  activate(): void {
    if (this.locked || this.cooldown > 0) return;
    this.emit("itemactivate", this.index);
  }

  #syncTooltip(): void {
    const theme = this.desktop?.theme;
    if (!this.#item || !theme) {
      this.tooltip = null;
      return;
    }
    this.tooltip = (this.describe ?? itemTooltip)(this.#item, theme, this.rarities);
  }

  protected override onEnvironmentChanged(): void {
    // The tooltip is built against the theme, so it has to be rebuilt when
    // the control finds one — or when the theme under it is swapped.
    this.#syncTooltip();
  }

  // --- drag and drop -------------------------------------------------------

  /** The payload this slot would hand over. Null when there is nothing to give. */
  dragPayload(): DragPayload | null {
    if (!this.#item || this.locked || !this.draggable) return null;
    const data: ItemDragData = { item: this.#item, fromIndex: this.index };
    return { kind: ITEM_DRAG_KIND, data, source: this };
  }

  override canAcceptDrop(payload: DragPayload): boolean {
    return this.droppable && !this.locked && payload.kind === ITEM_DRAG_KIND;
  }

  /**
   * The whole slot is the drag, so a scrollable pane behind it must not claim
   * the gesture and pan the inventory instead of moving the item.
   */
  override handlesDragAt(): boolean {
    return this.draggable && !!this.#item;
  }

  #onPointerDown(e: MouseEventArgs): void {
    if (e.button !== 0) return;
    this.#pressAt = { x: e.screen.x, y: e.screen.y };
  }

  #onPointerMove(e: MouseEventArgs): void {
    const from = this.#pressAt;
    if (!from || this.#dragging) return;
    if (Math.hypot(e.screen.x - from.x, e.screen.y - from.y) < DRAG_THRESHOLD) return;
    // Far enough to have meant it. Below the threshold this stays a click, so
    // a slot can be both "use this" and "move this".
    this.#pressAt = null;
    this.#beginDrag();
  }

  #beginDrag(): void {
    const payload = this.dragPayload();
    const desktop = this.desktop;
    if (!payload || !desktop) return;
    this.#dragging = true;
    desktop.beginDrag(payload, { ghost: this.#ghost() });
  }

  /** A copy of the slot's own face, following the pointer. */
  #ghost(): DragGhost {
    const item = this.#item;
    const slot = this;
    return new DragGhost({
      width: this.width,
      height: this.height,
      paint: (rc, theme, r) => {
        if (!item) return;
        slot.paintFace(rc, theme, r, item, 0.85);
      },
    });
  }

  #endPress(): void {
    this.#pressAt = null;
  }

  // --- painting ------------------------------------------------------------

  /** The square the icon is drawn in. */
  iconRect(): Rect {
    return {
      x: ICON_INSET,
      y: ICON_INSET,
      width: Math.max(0, this.width - ICON_INSET * 2),
      height: Math.max(0, this.height - ICON_INSET * 2),
    };
  }

  /**
   * The icon, rarity border and stack count — everything that belongs to the
   * *item* rather than to the slot. Shared with the drag ghost, so what
   * follows the pointer is what was picked up.
   */
  paintFace(rc: RenderContext, theme: Theme, r: Rect, item: ItemStack, alpha = 1): void {
    const rarity = rarityOf(item, this.rarities);
    const radius = theme.metrics.controlCornerRadius;

    rc.drawRect(r, {
      fill: withAlpha(rarity.color, 0.14 * alpha),
      radius,
      borderWidth: 1,
      borderColor: withAlpha(rarity.color, alpha),
    });

    const icon: Rect = {
      x: r.x + ICON_INSET,
      y: r.y + ICON_INSET,
      width: Math.max(0, r.width - ICON_INSET * 2),
      height: Math.max(0, r.height - ICON_INSET * 2),
    };
    if (item.sprite) {
      rc.drawTexture(item.sprite.texture, icon, item.sprite.source, {
        r: 1,
        g: 1,
        b: 1,
        a: alpha,
      });
    } else {
      // No art: the first letter of the name, in the rarity color. A slot
      // with an item in it must never look empty.
      const font = theme.metrics.captionFont;
      const initial = item.name.slice(0, 1).toUpperCase();
      const width = rc.measureText(initial, font).width;
      rc.drawText(
        initial,
        icon.x + (icon.width - width) / 2,
        icon.y + icon.height / 2,
        font,
        withAlpha(rarity.color, alpha),
        { baseline: "middle" },
      );
    }

    const count = item.count ?? 1;
    if (count > 1) {
      const font = this.countFont ?? theme.metrics.font;
      const text = String(count);
      const width = rc.measureText(text, font).width;
      rc.drawText(
        text,
        r.x + r.width - width - CORNER_PADDING,
        r.y + r.height - CORNER_PADDING,
        font,
        withAlpha(theme.palette.text, alpha),
        { baseline: "bottom" },
      );
    }
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const r = this.localRect;
    const enabled = this.effectivelyEnabled && !this.locked;

    theme.drawItemSlot(rc, r, {
      ...this.visualState(),
      filled: this.#item !== null,
      locked: this.locked,
      highlighted: this.highlighted,
      rarity: this.#item ? rarityOf(this.#item, this.rarities).color : null,
    });

    // The item is drawn faded in its home slot while it is being carried, so
    // the player can see where it came from.
    if (this.#item) this.paintFace(rc, theme, r, this.#item, this.#dragging ? 0.3 : 1);

    if (this.cooldownFraction > 0) {
      rc.fillArc(
        { x: r.width / 2, y: r.height / 2 },
        0,
        Math.hypot(r.width, r.height) / 2,
        0,
        Math.PI * 2 * this.cooldownFraction,
        withAlpha(p.desktop, 0.62),
      );
      const text = this.cooldown < 1 ? this.cooldown.toFixed(1) : String(Math.ceil(this.cooldown));
      const font = theme.metrics.captionFont;
      const width = rc.measureText(text, font).width;
      rc.drawText(text, (r.width - width) / 2, r.height / 2, font, p.text, { baseline: "middle" });
    }

    if (this.keybind) {
      const font = theme.metrics.font;
      rc.drawText(this.keybind, CORNER_PADDING, CORNER_PADDING, font, p.textSubtle, {
        baseline: "top",
      });
    }

    // Either this slot holds focus outright — a lone equipment slot — or it
    // is the cursor cell of a container that does.
    if ((this.focused || this.current) && enabled) theme.drawFocusRing(rc, r);
  }
}

/** A dim wash for a slot the player cannot use yet. Exposed for themes. */
export function lockedTint(theme: Theme): Color {
  return withAlpha(theme.palette.desktop, 0.5);
}
